import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  formatInsightPublishedAt,
  INSIGHT_V2_PLACEHOLDER_IMAGE,
  listInsightV2Posts,
} from '../features/insights-v2/content';
import { usePublicPublications } from '../hooks/usePublications';

const fallbackVisual = {
  image: '/images/CybardHeroMaybe.png',
  background:
    'radial-gradient(circle at top, rgba(157, 230, 255, 0.14), transparent 44%), linear-gradient(135deg, rgba(10, 18, 31, 0.96), rgba(18, 37, 60, 0.92))',
  imageFit: 'cover' as const,
  imagePosition: 'center center',
  imageScale: 1,
};

const Section = styled.section`
  padding: 12px 0 104px;
  background: #efecf0;
`;

const Inner = styled.div`
  width: min(1380px, calc(100% - 2rem));
  margin: 0 auto;
  display: grid;
  gap: 2.25rem;
`;

const Header = styled.div`
  display: grid;
  gap: 0.9rem;
  max-width: 46rem;

  @media (max-width: 760px) {
    justify-items: center;
    text-align: center;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  color: rgba(23, 33, 47, 0.64);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 0;
  color: #17212f;
  font-size: clamp(2.35rem, 5vw, 4rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
  text-wrap: balance;
`;

const Deck = styled.p`
  margin: 0;
  max-width: 42rem;
  color: #465263;
  font-size: 1rem;
  line-height: 1.65;
`;

const ShowcaseCard = styled.article`
  display: grid;
  grid-template-columns: minmax(300px, 0.92fr) minmax(0, 1.08fr);
  gap: clamp(1.5rem, 4vw, 3.5rem);
  align-items: center;
  padding: 2rem 0;
  border-top: 1px solid rgba(23, 33, 47, 0.12);
  border-bottom: 1px solid rgba(23, 33, 47, 0.12);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Visual = styled.div<{
  $background: string;
  $image: string;
  $imageFit: 'cover' | 'contain';
  $imagePosition: string;
  $imageScale: number;
}>`
  position: relative;
  min-height: clamp(320px, 42vw, 460px);
  overflow: hidden;
  border-radius: 28px;
  background: ${({ $background }) => $background};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(8, 11, 19, 0.08) 0%, rgba(8, 11, 19, 0.18) 56%, rgba(8, 11, 19, 0.74) 100%),
      url(${({ $image }) => $image});
    background-repeat: no-repeat;
    background-position: ${({ $imagePosition }) => $imagePosition};
    background-size: ${({ $imageFit, $imageScale }) =>
      $imageFit === 'contain' ? `${$imageScale * 100}% auto` : `${$imageScale * 100}% ${$imageScale * 100}%`};
    transform: scale(1.01);
    opacity: 0.92;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.06), transparent 42%),
      linear-gradient(180deg, rgba(7, 12, 20, 0) 28%, rgba(7, 12, 20, 0.22) 55%, rgba(7, 12, 20, 0.82) 100%);
  }
`;

const VisualLabel = styled.span`
  position: absolute;
  left: 1.3rem;
  top: 1.3rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: 0.42rem 0.78rem;
  border-radius: 999px;
  background: rgba(18, 14, 10, 0.55);
  border: 1px solid rgba(157, 230, 255, 0.34);
  color: #d7f3ff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const VisualTitle = styled.h3`
  position: absolute;
  inset: auto 1.3rem 1.25rem;
  z-index: 1;
  margin: 0;
  color: #fbf6ef;
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 0.92;
  letter-spacing: -0.05em;
  text-wrap: balance;
`;

const Content = styled.div`
  display: grid;
  align-content: start;
  gap: 1.1rem;
  max-width: 34rem;
`;

const PostTitle = styled.h3`
  margin: 0;
  color: #17212f;
  font-size: clamp(1.9rem, 3vw, 2.8rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
  text-wrap: balance;
`;

const PostDeck = styled.p`
  margin: 0;
  color: #17212f;
  font-size: 1.08rem;
  line-height: 1.65;
`;

const PostNote = styled.p`
  margin: 0;
  color: #465263;
  font-size: 0.98rem;
  line-height: 1.7;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 0.45rem;
`;

const ActionLink = styled(Link)`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0.78rem 1.15rem;
  border-radius: 999px;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.68);
  color: #17212f;
  font-size: 0.96rem;
  font-weight: 700;
  text-decoration: none;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    transform 160ms ease;

  &:hover {
    color: #214a63;
    border-color: rgba(33, 74, 99, 0.42);
    background: rgba(255, 255, 255, 0.92);
    transform: translateY(-1px);
  }
`;

export default function BlogSection() {
  const publicPublications = usePublicPublications();
  const featuredPost = listInsightV2Posts(publicPublications.data).at(0);

  if (!featuredPost) {
    return null;
  }

  const visual = {
    image: featuredPost.coverImageUrl || INSIGHT_V2_PLACEHOLDER_IMAGE,
    background: featuredPost.theme.surface || fallbackVisual.background,
    imageFit: 'cover' as const,
    imagePosition: 'center center',
    imageScale: 1,
  };

  return (
    <Section>
      <Inner>
        <Header>
          <Eyebrow>Blog</Eyebrow>
          <Title>Fresh Notes from the Studio</Title>
          <Deck>
            The latest piece from the CyBard journal, with a direct path into the full archive of
            strategic writing and long-form articles.
          </Deck>
        </Header>

        <ShowcaseCard>
            <Visual
              $background={visual.background}
              $image={visual.image}
            $imageFit={visual.imageFit ?? 'cover'}
            $imagePosition={visual.imagePosition ?? 'center center'}
            $imageScale={visual.imageScale ?? 1}
          >
            <VisualLabel>Most Recent</VisualLabel>
            <VisualTitle>{featuredPost.category}</VisualTitle>
          </Visual>

          <Content>
            <PostTitle>{featuredPost.title}</PostTitle>
            <PostDeck>{featuredPost.subtitle || featuredPost.excerpt}</PostDeck>
            <PostNote>
              {formatInsightPublishedAt(featuredPost.publishedAt)}. Visit the journal to explore
              more CyBard writing on brand, commerce, systems, and digital strategy.
            </PostNote>

            <Actions>
              <ActionLink to="/publications">Visit Journal</ActionLink>
            </Actions>
          </Content>
        </ShowcaseCard>
      </Inner>
    </Section>
  );
}
